import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  Typography, MenuItem, Switch, FormControlLabel, CircularProgress, Alert, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MarkEmailRead, WifiTethering } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getTenantMailSettings, saveTenantMailSettings, testTenantMailSettings,
} from '../../services/tenants';

// Os valores que o `imap_open()` da API entende na string de conexão.
const ENCRIPTACOES = [
  { valor: 'ssl', rotulo: 'SSL (porta 993)' },
  { valor: 'tls', rotulo: 'TLS' },
  { valor: 'notls', rotulo: 'Sem criptografia' },
];

const VAZIO = {
  host: '', port: 993, username: '', password: '',
  encryption: 'ssl', folder: 'INBOX', enabled: true,
};

/**
 * Caixa de e-mail de uma empresa.
 *
 * A senha nunca volta da API -- só a informação de que existe uma. Por isso o
 * campo fica em branco ao editar e o placeholder diz o que acontece se ficar
 * assim: mantém a atual.
 */
const MailSettingsDialog = ({ open, tenant, onClose }) => {
  const theme = useTheme();
  const [form, setForm] = useState(VAZIO);
  const [existente, setExistente] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [erros, setErros] = useState({});

  useEffect(() => {
    if (!open || !tenant) return;

    let cancelado = false;
    setCarregando(true);
    setErros({});

    getTenantMailSettings(tenant.id)
      .then((dados) => {
        if (cancelado) return;
        setExistente(dados);
        setForm(dados
          ? { ...dados, password: '' }
          : { ...VAZIO, username: '' });
      })
      .catch(() => {
        if (!cancelado) toast.error('Não foi possível carregar a caixa de e-mail.');
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => { cancelado = true; };
  }, [open, tenant]);

  const alterar = (campo) => (e) => {
    const valor = campo === 'enabled' ? e.target.checked : e.target.value;
    setForm((atual) => ({ ...atual, [campo]: valor }));
    setErros((atual) => ({ ...atual, [campo]: undefined }));
  };

  const salvar = async () => {
    setSalvando(true);
    setErros({});
    try {
      const salvo = await saveTenantMailSettings(tenant.id, {
        host: form.host,
        port: Number(form.port),
        username: form.username,
        // Em branco significa "mantém a que está" -- a API trata assim.
        password: form.password || undefined,
        encryption: form.encryption,
        folder: form.folder || 'INBOX',
        enabled: !!form.enabled,
      });
      setExistente(salvo);
      setForm((atual) => ({ ...atual, password: '' }));
      toast.success('Caixa de e-mail salva.', { position: toast.POSITION.BOTTOM_CENTER });
    } catch (error) {
      const msg = error?.response?.data?.msg;
      if (msg && typeof msg === 'object') {
        setErros(Object.keys(msg).reduce((acc, k) => ({ ...acc, [k]: msg[k][0] }), {}));
      } else {
        toast.error('Não foi possível salvar a caixa de e-mail.', { position: toast.POSITION.BOTTOM_CENTER });
      }
    } finally {
      setSalvando(false);
    }
  };

  const testar = async () => {
    setTestando(true);
    try {
      const r = await testTenantMailSettings(tenant.id);
      if (r.ok) {
        toast.success('Conexão estabelecida com sucesso.', { position: toast.POSITION.BOTTOM_CENTER });
      } else {
        toast.error(r.error, { position: toast.POSITION.BOTTOM_CENTER, autoClose: 8000 });
      }
      setExistente(await getTenantMailSettings(tenant.id));
    } catch (error) {
      toast.error('Não foi possível testar a conexão.', { position: toast.POSITION.BOTTOM_CENTER });
    } finally {
      setTestando(false);
    }
  };

  const campo = (nome, rotulo, extras = {}) => (
    <TextField
      size="small"
      fullWidth
      label={rotulo}
      value={form[nome] ?? ''}
      onChange={alterar(nome)}
      error={!!erros[nome]}
      helperText={erros[nome]}
      {...extras}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MarkEmailRead fontSize="small" />
        Caixa de e-mail — {tenant?.name}
      </DialogTitle>

      <DialogContent dividers>
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Os e-mails não lidos desta caixa viram chamados desta empresa, a cada 5 minutos.
              O chamado entra como Pendente, para triagem.
            </Typography>

            {existente?.last_error && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                A última verificação falhou: {existente.last_error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 2 }}>{campo('host', 'Servidor IMAP', { placeholder: 'imap.suaempresa.com.br' })}</Box>
              <Box sx={{ flex: 1 }}>{campo('port', 'Porta', { type: 'number' })}</Box>
            </Box>

            {campo('username', 'Usuário', { placeholder: 'suporte@suaempresa.com.br' })}

            {campo('password', 'Senha', {
              type: 'password',
              placeholder: existente?.has_password ? 'Deixe em branco para manter a atual' : '',
              helperText: erros.password
                || (existente?.has_password ? 'Em branco mantém a senha já gravada.' : 'Obrigatória na primeira configuração.'),
            })}

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                size="small"
                fullWidth
                label="Criptografia"
                value={form.encryption}
                onChange={alterar('encryption')}
                sx={{ flex: 1 }}
              >
                {ENCRIPTACOES.map((e) => (
                  <MenuItem key={e.valor} value={e.valor}>{e.rotulo}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ flex: 1 }}>{campo('folder', 'Pasta', { placeholder: 'INBOX' })}</Box>
            </Box>

            <Divider />

            <FormControlLabel
              control={<Switch checked={!!form.enabled} onChange={alterar('enabled')} />}
              label="Buscar e-mails desta caixa"
            />

            {existente?.last_checked_at && !existente?.last_error && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Última verificação bem-sucedida em{' '}
                {new Date(existente.last_checked_at).toLocaleString('pt-BR')}.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={testar}
          disabled={testando || salvando || !existente}
          startIcon={testando ? <CircularProgress size={14} color="inherit" /> : <WifiTethering />}
          sx={{ textTransform: 'none', mr: 'auto' }}
        >
          Testar conexão
        </Button>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Fechar</Button>
        <Button
          variant="contained"
          onClick={salvar}
          disabled={salvando || carregando}
          startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MailSettingsDialog;
